import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import aboutImg from '../images/aboutus.png';

const AboutUs = ({ onApplyClick }) => {
  const handleImageError = (e) => {
    e.target.src = '/images/placeholder-aboutUs.jpg';
  };

  useEffect(() => {
    const initAOS = async () => {
      try {
        const AOS = (await import('https://cdn.jsdelivr.net/npm/aos@2.3.4/dist/aos.js')).default;
        if (AOS && typeof AOS.init === 'function') {
          AOS.init({ 
            duration: 800,
            once: true,
            easing: 'ease-out-cubic'
          });
        }
      } catch (error) {
        console.log('AOS loading skipped');
      }
    };
    initAOS();
    
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/aos@2.3.4/dist/aos.css';
    document.head.appendChild(link);
  }, []);

  return (
    <div id="about" className="relative z-20 bg-white py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
        
        {/* Section Header */}
        <div className="text-center mb-6 sm:mb-8 md:mb-10 lg:mb-12" data-aos="fade-up">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight text-gray-800 mb-2">
            <span
              className="font-bold text-transparent bg-clip-text"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
              }}
            >
              About
            </span>
            <span className="font-extralight"> us</span>
          </h2>
        </div>

        {/* Image - Shows only on mobile, directly under title */}
        <div className="block lg:hidden mb-6 sm:mb-8" data-aos="zoom-in" data-aos-delay="100">
          <div className="w-full max-w-md mx-auto">
            <img
              src={aboutImg}
              alt="About PUP PYLON TBI"
              onError={handleImageError}
              className="w-full h-48 sm:h-64 object-cover rounded-lg shadow-md"
            />
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 xl:gap-20 items-center">
          
          {/* Left Side - Image (Desktop only) */}
          <div className="hidden lg:block" data-aos="fade-right" data-aos-delay="100">
            <div className="w-full">
              <img
                src={aboutImg}
                alt="About PUP PYLON TBI"
                onError={handleImageError}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8" data-aos="fade-left" data-aos-delay="200">
            {/* Description Text */}
            <div className="text-xs sm:text-sm md:text-base text-justify text-gray-700 leading-relaxed space-y-3 sm:space-y-4">
              <p>
                A Technology Business Incubator (TBI) is a support system for startups, providing resources like mentorship, funding, office space, and networking opportunities to foster growth and success, particularly in technology-driven industries.
              </p>
              <p>
                Our objective is to support and nurture early-stage technology-based startups by providing them with a comprehensive ecosystem that fosters innovation and entrepreneurship.
              </p>
              <p>
                Dedicated to transforming innovative ideas into viable, market-ready products and services.
              </p>
            </div>

            {/* Statistics */}
            <div className="w-full flex justify-center pt-4 sm:pt-6" data-aos="fade-up" data-aos-delay="300">
              <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8 max-w-lg mx-auto w-full">

                <div className="text-center" data-aos="zoom-in" data-aos-delay="400">
                  <h3
                    className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text"
                    style={{
                      backgroundImage:
                        "linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)",
                    }}
                  >
                    2500+
                  </h3>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 mt-1">Individuals Participated</p>
                </div>

                <div className="text-center" data-aos="zoom-in" data-aos-delay="500">
                  <h3
                    className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text"
                    style={{
                      backgroundImage:
                        "linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)",
                    }}
                  >
                    32+
                  </h3>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 mt-1">Applicants</p>
                </div>

                <div className="text-center" data-aos="zoom-in" data-aos-delay="600">
                  <h3
                    className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text"
                    style={{
                      backgroundImage:
                        "linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)",
                    }}
                  >
                    9+
                  </h3>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 mt-1">Incubated</p>
                </div>

              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 pt-3 sm:pt-4" data-aos="fade-up" data-aos-delay="700">
              <button onClick={onApplyClick} className="w-full sm:w-auto px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 border border-gray-800 text-gray-800 rounded-full hover:bg-gray-800 hover:text-white transition text-xs sm:text-sm md:text-base">
                Apply Now
              </button>
              <Link to="/programs" className="w-full sm:w-auto px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 border border-gray-800 text-gray-800 rounded-full hover:bg-gray-800 hover:text-white transition text-xs sm:text-sm md:text-base">
                Check out our programs!
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;